import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { Typography, Box, Button, Checkbox, Link } from '@mui/material';
import { styled } from '@mui/material/styles';

import { ALL_ROUTES as WEBEX_ROUTES } from 'src/webex/routes';
import { ModalDialog } from 'src/components/ModalDialog';
import { useConsents } from 'src/contexts/ConsentsContext';
import { useServerRecognition } from 'src/hooks/useServerRecognition';
import {
  PRIVACY_POLICY_LINK,
  SUPPORT_EMAIL,
  TERMS_OF_SERVICE_LINK,
} from 'src/constants';
import { getActionButtonDefaultSX } from './ActionButtonSX';
import { clientApi } from 'src/api/clientApi';
import useAuth from 'src/hooks/useAuth';

const IGNORE_PATHS = [...WEBEX_ROUTES, '/sign-up'];

const validationSchema = Yup.object().shape({
  isAgreed: Yup.boolean().required().oneOf([true], 'It is required to agree'),
});

const linkProps = {
  target: '_blank',
  rel: 'noopener',
};

const FieldErrorTypography = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: '#d32f2f',
  backgroundColor: '#FFF4F2',
  padding: '0px 14px',
}));

const ActionButton = styled(Button)(() => {
  return {
    height: 46,
    fontSize: 16,
    fontWeight: 700,
  };
});

const MainConsentsPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const consents = useConsents();
  const { updateUser } = useAuth();
  const ssr = useServerRecognition();

  useEffect(() => {
    const isIgnoredPath = IGNORE_PATHS.includes(window.location.pathname);
    if (isIgnoredPath) {
      return;
    }

    setIsOpen(consents.missingMainConsents);
  }, [consents.missingMainConsents]);

  const handleFormSubmit = async (values) => {
    try {
      const updates = await clientApi.updateConsents({
        accepted_tos: values.isAgreed,
        accepted_privacy_policy: values.isAgreed,
        marketing_consent: values.isAgreedToMarketing,
      });
      updateUser(updates);
      ssr.reConnect(); //server recognition is blocked when TOS and Privacy policy are not accepted
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  return (
    <ModalDialog
      isOpen={isOpen || false}
      title={'We’ve updated our Terms and Privacy Policy.'}
      content={
        <Formik
          validationSchema={validationSchema}
          initialValues={{
            isAgreed: false,
            isAgreedToMarketing: false,
          }}
          onSubmit={handleFormSubmit}
        >
          {(formikProps) => {
            const {
              values,
              handleChange,
              setFieldValue,
              errors,
              touched,
              handleBlur,
              isValid,
              dirty,
            } = formikProps;

            const isActionButtonDisabled = !(dirty && isValid);
            const actionButtonSX = getActionButtonDefaultSX(
              isActionButtonDisabled,
              false,
            );
            return (
              <Form>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 18,
                    }}
                  >
                    To continue using the service, please review and accept our
                    updated Privacy Policy and Terms & Conditions. These updates
                    clarify how we collect, use, and protect your data.
                  </Typography>
                  <div>
                    <Typography
                      variant="body2"
                      mt={4}
                      id="isAgreedToMarketing_label"
                    >
                      <Checkbox
                        id="isAgreedToMarketing"
                        inputProps={{
                          'aria-labelledby': 'isAgreedToMarketing_label',
                        }}
                        sx={{ padding: '0px 9px 0px 0px' }}
                        value={values.isAgreedToMarketing}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      I agree to receive occasional marketing communications. I
                      understand I can unsubscribe at any time by contacting{' '}
                      <Link
                        target="_blank"
                        fontSize={14}
                        href={`mailto:${SUPPORT_EMAIL}`}
                      >
                        {SUPPORT_EMAIL}
                      </Link>
                    </Typography>
                    <Typography variant="body2" mt={1} id="isAgreed_label">
                      <Checkbox
                        id="isAgreed"
                        inputProps={{ 'aria-labelledby': 'isAgreed_label' }}
                        sx={{ padding: '0px 9px 0px 0px' }}
                        value={values.isAgreed}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      I have read and agree to the updated{' '}
                      <Link {...linkProps} href={TERMS_OF_SERVICE_LINK}>
                        Terms & Conditions
                      </Link>{' '}
                      and{' '}
                      <Link {...linkProps} href={PRIVACY_POLICY_LINK}>
                        Privacy Policy
                      </Link>
                    </Typography>
                    <FieldErrorTypography>
                      {touched.isAgreed && errors.isAgreed}
                    </FieldErrorTypography>
                  </div>
                  <ActionButton
                    variant="contained"
                    type="submit"
                    disabled={isActionButtonDisabled}
                    sx={{
                      ...actionButtonSX,
                      mt: 2,
                    }}
                  >
                    Accept
                  </ActionButton>
                </Box>
              </Form>
            );
          }}
        </Formik>
      }
    />
  );
};

export default MainConsentsPopup;
